import { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, User, Bot, Minimize2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/webchat`;

export default function WebchatWidget() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState('');
  const [visitorToken, setVisitorToken] = useState('');
  const [status, setStatus] = useState('');
  const [owner, setOwner] = useState('');
  const [unread, setUnread] = useState(0);
  const msgsEnd = useRef(null);
  const pollRef = useRef(null);

  // Load config + session
  useEffect(() => {
    fetch(`${API}/config`).then(r => r.json()).then(setConfig).catch(() => {});
    const stored = localStorage.getItem('recupnet_webchat_token');
    if (stored) {
      setVisitorToken(stored);
      fetch(`${API}/session`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({visitorToken:stored})})
        .then(r => r.json()).then(d => {
          if (d.conversation) { setConvId(d.conversation.id); setStatus(d.conversation.status); setOwner(d.conversation.currentOwner); }
        }).catch(() => {});
    }
  }, []);

  // Polling for new messages
  useEffect(() => {
    if (!convId || !open) return;
    const poll = async () => {
      try {
        const last = messages.length ? messages[messages.length-1].createdAt : '';
        const r = await fetch(`${API}/messages/${convId}${last ? '?after='+last : ''}`);
        const d = await r.json();
        if (d.messages?.length) {
          setMessages(prev => {
            const ids = new Set(prev.map(m => m.id));
            const newMsgs = d.messages.filter(m => !ids.has(m.id));
            if (newMsgs.length && !open) setUnread(u => u + newMsgs.length);
            return newMsgs.length ? [...prev, ...newMsgs] : prev;
          });
        }
        if (d.status) setStatus(d.status);
        if (d.owner) setOwner(d.owner);
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [convId, open]);

  useEffect(() => { msgsEnd.current?.scrollIntoView({behavior:'smooth'}); }, [messages]);
  useEffect(() => { if (open) setUnread(0); }, [open]);

  const startSession = async () => {
    const params = new URLSearchParams(window.location.search);
    const r = await fetch(`${API}/session`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({
      sourceUrl: window.location.href, landingPage: window.location.pathname,
      utmSource: params.get('utm_source')||'', utmMedium: params.get('utm_medium')||'', utmCampaign: params.get('utm_campaign')||'',
      userAgent: navigator.userAgent,
    })});
    const d = await r.json();
    setVisitorToken(d.visitorToken);
    localStorage.setItem('recupnet_webchat_token', d.visitorToken);
    if (d.conversation) { setConvId(d.conversation.id); }
    return d.visitorToken;
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    setSending(true);
    let token = visitorToken;
    if (!token) token = await startSession();
    try {
      const r = await fetch(`${API}/messages`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({visitorToken:token,text:input})});
      const d = await r.json();
      if (d.conversationId) setConvId(d.conversationId);
      setMessages(prev => {
        const ids = new Set(prev.map(m => m.id));
        const add = [];
        if (d.message && !ids.has(d.message.id)) add.push(d.message);
        if (d.aiResponse && !ids.has(d.aiResponse.id)) add.push(d.aiResponse);
        return [...prev, ...add];
      });
      setInput('');
    } catch {} finally { setSending(false); }
  };

  const color = config?.primaryColor || '#63CBB6';

  if (!config?.enabled && config !== null) return null;

  return (
    <div style={{position:'fixed',bottom:24,right:24,zIndex:9999,fontFamily:'Inter,sans-serif'}} data-testid="webchat-widget">
      {/* Launcher */}
      {!open && (
        <button onClick={() => {setOpen(true); if(!visitorToken) startSession();}} data-testid="webchat-launcher"
          style={{width:56,height:56,borderRadius:28,background:color,border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 4px 20px rgba(0,0,0,0.3)',position:'relative'}}>
          <MessageSquare size={24} color="#0B0F10"/>
          {unread > 0 && <span style={{position:'absolute',top:-4,right:-4,width:20,height:20,borderRadius:10,background:'#D32F2F',color:'white',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700}}>{unread}</span>}
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div style={{width:380,height:560,borderRadius:18,overflow:'hidden',display:'flex',flexDirection:'column',background:'#0B0F10',border:'1px solid rgba(99,203,182,0.15)',boxShadow:'0 12px 40px rgba(0,0,0,0.5)'}}>
          {/* Header */}
          <div style={{padding:'14px 16px',background:'rgba(15,20,24,0.95)',borderBottom:'1px solid rgba(255,255,255,0.08)',display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:36,height:36,borderRadius:18,background:`${color}22`,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <Bot size={18} color={color}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600,color:'white'}}>{config?.agentName || 'RecupBot'}</div>
              <div style={{fontSize:11,color:'rgba(255,255,255,0.4)'}}>{owner==='human'?'Atendente humano conectado':config?.subtitle || 'Atendimento RecupNet'}</div>
            </div>
            <button onClick={()=>setOpen(false)} style={{background:'none',border:'none',cursor:'pointer',padding:4}}><Minimize2 size={16} color="rgba(255,255,255,0.4)"/></button>
          </div>

          {/* Messages */}
          <div style={{flex:1,overflowY:'auto',padding:12,display:'flex',flexDirection:'column',gap:8}}>
            {messages.length === 0 && config?.welcomeMessage && (
              <div style={{display:'flex',gap:8,maxWidth:'85%'}}>
                <div style={{padding:'10px 14px',borderRadius:'14px 14px 14px 4px',background:`${color}18`,fontSize:13,color:'rgba(255,255,255,0.85)',lineHeight:1.5}}>
                  <div style={{fontSize:10,color:color,marginBottom:4,fontWeight:600}}>RecupBot</div>
                  {config.welcomeMessage}
                </div>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} style={{display:'flex',justifyContent:m.senderType==='customer'?'flex-end':'flex-start'}}>
                <div style={{maxWidth:'80%',padding:'10px 14px',borderRadius:m.senderType==='customer'?'14px 14px 4px 14px':'14px 14px 14px 4px',background:m.senderType==='customer'?`${color}30`:m.contentType==='system'?'rgba(255,255,255,0.04)':m.isAI?`${color}15`:'rgba(99,203,182,0.25)',fontSize:13,color:m.contentType==='system'?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.85)',lineHeight:1.5,fontStyle:m.contentType==='system'?'italic':'normal'}}>
                  {m.senderType!=='customer' && m.contentType!=='system' && <div style={{fontSize:10,color:m.isAI?color:'rgba(99,203,182,0.8)',marginBottom:3,fontWeight:600}}>{m.isAI?'RecupBot':m.senderName||'Atendente'}</div>}
                  {m.content}
                  <div style={{fontSize:9,color:'rgba(255,255,255,0.2)',marginTop:4,textAlign:'right'}}>{m.createdAt?new Date(m.createdAt).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}):''}</div>
                </div>
              </div>
            ))}
            <div ref={msgsEnd}/>
          </div>

          {/* Status banner */}
          {status==='waiting_human' && (
            <div style={{padding:'8px 16px',background:'rgba(249,168,37,0.08)',borderTop:'1px solid rgba(249,168,37,0.15)',fontSize:11,color:'rgba(249,168,37,0.8)',textAlign:'center'}}>
              Aguardando atendente humano...
            </div>
          )}

          {/* Input */}
          {status!=='closed' && (
            <div style={{padding:'10px 12px',borderTop:'1px solid rgba(255,255,255,0.08)',display:'flex',gap:8,background:'rgba(15,20,24,0.8)'}}>
              <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendMessage()}
                placeholder="Digite sua mensagem..." disabled={sending}
                style={{flex:1,background:'rgba(0,0,0,0.2)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:12,padding:'10px 14px',color:'white',fontSize:13,outline:'none'}}/>
              <button onClick={sendMessage} disabled={sending||!input.trim()}
                style={{width:40,height:40,borderRadius:12,background:color,border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',opacity:input.trim()?1:0.3}}>
                {sending?<div className="rn-spinner" style={{width:14,height:14}}/>:<Send size={16} color="#0B0F10"/>}
              </button>
            </div>
          )}

          {/* Footer */}
          <div style={{padding:'6px 12px',textAlign:'center',fontSize:9,color:'rgba(255,255,255,0.15)',borderTop:'1px solid rgba(255,255,255,0.04)'}}>
            Powered by RecupNet
          </div>
        </div>
      )}
    </div>
  );
}
